import React, { useCallback, useMemo, useState } from 'react';
import { VirtualizedList } from 'react-native';

import { DEPOSIT_OPTIONS } from '@webtrader/core/constants/experienceDemo';
import { IDemoAccountConfigOption } from '@webtrader/core/constants/experienceDemo/types';
import { EDataTestId } from '@webtrader/core/constants/test';
import { useTranslation } from '@webtrader/core/i18n';

import ActionSheetCheckmark from '@/components/ActionSheet/Checkmark';
import ActionSheetSelector from '@/components/ActionSheet/Selectors';
import ValidationError from '@/components/FormValidationError';
import Modal from '@/components/Modal';

import {
  StyledActionSheet,
  StyledDropdownSelectorContainer,
  StyledDropdownSelectorIcon,
  StyledDropdownSelectorText,
  StyledFormField,
  StyledFormTitle,
  StyledFormTitleContainer,
  StyledInputWrapper,
  StyledIonicons
} from '../styled';

interface IDepositDropdownProps {
  title: string;
  value?: number;
  validationText: string;
  showValidation: boolean;
  onChange?: (value: number) => void;
}

const DepositDropdown: FCC<IDepositDropdownProps> = (props) => {
  const { title, value, validationText, showValidation, onChange } = props;

  const { t } = useTranslation();

  const [showOptions, setShowOptions] = useState(false);

  const onOpen = () => setShowOptions(true);

  const onClose = () => setShowOptions(false);

  const onSelectDepositAmount = useCallback(
    (amount: number) => {
      onChange?.(amount);

      onClose();
    },
    [onChange]
  );

  const depositLabel = useMemo(() => {
    const option = DEPOSIT_OPTIONS.find((opt) => opt.value === value);
    return option ? option.label : undefined;
  }, [value]);

  return (
    <StyledFormField isPaddingBottom>
      <StyledFormTitleContainer>
        <StyledIonicons name="wallet-outline" size={24} />
        <StyledFormTitle>{title}</StyledFormTitle>
      </StyledFormTitleContainer>
      <StyledInputWrapper>
        <StyledActionSheet onPress={onOpen}>
          <ActionSheetSelector
            label={depositLabel}
            withBorder={true}
            testID={EDataTestId.DEMO_ACCOUNT_CREATION_MODAL_DEPOSIT}
          />
        </StyledActionSheet>
        <Modal title={t('Deposit Amount')} visible={showOptions} onClose={onClose}>
          <VirtualizedList<IDemoAccountConfigOption>
            showsVerticalScrollIndicator={false}
            data={DEPOSIT_OPTIONS}
            getItem={(data, i) => data[i]}
            getItemCount={() => DEPOSIT_OPTIONS.length}
            keyExtractor={({ value }) => value.toString()}
            initialNumToRender={30}
            renderItem={({ item }) => {
              const { label, value: optionValue } = item;
              const selected = optionValue === value;

              return (
                <StyledDropdownSelectorContainer
                  selected={selected}
                  activeOpacity={1}
                  onPress={() => onSelectDepositAmount(optionValue as number)}
                  testID={EDataTestId.DEPOSIT_DROPDOWN_ITEM}
                >
                  <StyledDropdownSelectorText>{label}</StyledDropdownSelectorText>
                  <StyledDropdownSelectorIcon>{selected && <ActionSheetCheckmark />}</StyledDropdownSelectorIcon>
                </StyledDropdownSelectorContainer>
              );
            }}
          />
        </Modal>
        <ValidationError showValidation={showValidation} message={validationText} />
      </StyledInputWrapper>
    </StyledFormField>
  );
};

export default DepositDropdown;
